import { X } from 'lucide-react';
import {
  type ClipboardEvent,
  type KeyboardEvent,
  type RefObject,
  useId,
  useRef,
  useState,
} from 'react';

import { cx } from '@/lib/cx';

import {
  acrescentarTrechos,
  avisoDoAcrescimo,
  SEPARADOR_PADRAO,
  separarTrechos,
} from './lista-chips';

interface CampoListaChipsProps {
  valores: readonly string[];
  aoMudar: (valores: string[]) => void;
  /** Texto digitado → valor gravado (ex.: "2710.12.59" → "27101259"); nulo recusa. */
  interpretar?: (texto: string) => string | null;
  /** Valor gravado → como aparece no chip (ex.: "27101259" → "2710.12.59"). */
  formatar?: (valor: string) => string;
  /** Onde cortar o que foi colado ou digitado (padrão: linha, `;` e `,`). */
  separador?: RegExp;
  maximo?: number;
  /** Fonte de dados (códigos) nos chips. */
  mono?: boolean;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  className?: string;
  disabled?: boolean;
}

const identidade = (t: string): string | null => t;
const semFormato = (v: string) => v;

/** A caixa tem o visual dos campos: anel de foco quando o campo de digitação está focado. */
function estiloCaixa(invalido: boolean, desabilitado: boolean): string {
  return cx(
    'flex min-h-controle w-full flex-wrap items-center gap-1 rounded-controle border border-borda-forte bg-superficie px-1.5 py-1 shadow-baixa',
    'transition-[border-color,box-shadow] duration-[var(--duracao-rapida)] focus-within:border-marca focus-within:ring-3 focus-within:ring-marca-suave',
    invalido && 'border-perigo',
    desabilitado && 'cursor-not-allowed opacity-60',
  );
}

/** Texto em digitação e aviso; confirma, remove e trata teclas e colagem. */
function useListaChips(p: CampoListaChipsProps, campo: RefObject<HTMLInputElement | null>) {
  const { valores, aoMudar } = p;
  const interpretar = p.interpretar ?? identidade;
  const separador = p.separador ?? SEPARADOR_PADRAO;
  const maximo = p.maximo ?? 20;
  const [texto, setTexto] = useState('');
  const [aviso, setAviso] = useState('');

  const confirmar = (entrada: string) => {
    const trechos = separarTrechos(entrada, separador);
    if (trechos.length === 0) return;
    const resultado = acrescentarTrechos(valores, trechos, interpretar, maximo);
    if (resultado.valores.length !== valores.length) aoMudar(resultado.valores);
    setAviso(avisoDoAcrescimo(resultado, maximo));
    setTexto(resultado.recusados.join('; '));
  };

  const aoTeclar = (e: KeyboardEvent<HTMLInputElement>) => {
    const confirma = e.key === ';' || e.key === ',' || (e.key === 'Enter' && texto.trim() !== '');
    if (confirma) {
      e.preventDefault();
      confirmar(texto);
    } else if (e.key === 'Backspace' && texto === '' && valores.length > 0) {
      aoMudar(valores.slice(0, -1));
    }
  };

  const aoColar = (e: ClipboardEvent<HTMLInputElement>) => {
    const colado = e.clipboardData.getData('text');
    if (separarTrechos(colado, separador).length < 2) return;
    e.preventDefault();
    confirmar(`${texto}\n${colado}`);
  };

  const remover = (valor: string) => {
    aoMudar(valores.filter((v) => v !== valor));
    campo.current?.focus();
  };

  return { texto, setTexto, aviso, confirmar, aoTeclar, aoColar, remover };
}

/**
 * Lista de valores em chips (NCM, CFOP, CNPJ, trechos de texto). Enter, `;`, `,` ou sair do campo
 * confirmam; colar uma lista inteira vira vários chips de uma vez; Backspace no campo vazio tira o
 * último. O que não for aceito é dito numa região `polite`, nunca some em silêncio.
 */
export function CampoListaChips(props: CampoListaChipsProps) {
  const { valores, mono = false, disabled = false, className } = props;
  const formatar = props.formatar ?? semFormato;
  const rotulo = props['aria-label'];
  const campo = useRef<HTMLInputElement>(null);
  const { texto, setTexto, aviso, confirmar, aoTeclar, aoColar, remover } = useListaChips(
    props,
    campo,
  );
  const idAviso = useId();
  const descricao = [props['aria-describedby'], aviso ? idAviso : undefined]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cx('grid gap-1', className)}>
      <div
        className={estiloCaixa(props['aria-invalid'] === true, disabled)}
        onClick={() => campo.current?.focus()}
      >
        <ul className="contents" aria-label={`${rotulo ?? 'Lista'}: valores`}>
          {valores.map((v) => (
            <Chip
              key={v}
              rotulo={formatar(v)}
              mono={mono}
              disabled={disabled}
              aoRemover={() => {
                remover(v);
              }}
            />
          ))}
        </ul>
        <input
          ref={campo}
          id={props.id}
          aria-label={rotulo}
          aria-invalid={props['aria-invalid']}
          aria-describedby={descricao || undefined}
          disabled={disabled}
          value={texto}
          placeholder={valores.length === 0 ? props.placeholder : undefined}
          className={cx(
            'h-controle-sm min-w-24 flex-1 bg-transparent px-1.5 text-base text-texto outline-none placeholder:text-texto-fraco',
            mono && 'font-mono',
          )}
          onChange={(e) => {
            setTexto(e.target.value);
          }}
          onKeyDown={aoTeclar}
          onPaste={aoColar}
          onBlur={() => {
            confirmar(texto);
          }}
        />
      </div>
      <p id={idAviso} role="status" className="text-xs text-alerta empty:hidden">
        {aviso}
      </p>
    </div>
  );
}

function Chip(p: { rotulo: string; mono: boolean; disabled: boolean; aoRemover: () => void }) {
  return (
    <li className="inline-flex h-6 items-center gap-0.5 rounded-mini bg-marca-suave pl-2 text-sm text-marca animate-surgir">
      <span className={cx('tabular', p.mono && 'font-mono')}>{p.rotulo}</span>
      <button
        type="button"
        disabled={p.disabled}
        aria-label={`Remover ${p.rotulo}`}
        className="grid size-6 cursor-pointer place-items-center rounded-mini text-marca/70 transition-colors duration-[var(--duracao-rapida)] hover:text-marca focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave disabled:cursor-not-allowed [&_svg]:size-3.5"
        onClick={(e) => {
          e.stopPropagation();
          p.aoRemover();
        }}
      >
        <X aria-hidden />
      </button>
    </li>
  );
}
